import { memo, useRef } from "react";

import track, { useTracking } from "react-tracking";

import { BuiNumberInput } from "@btnet/blueprint-components";
import {
  CurrencyLocale,
  getAccuracy,
  isNullOrUndefined,
  round,
} from "@btnet/utilities-core";

import { NumberDisplay } from "../NumberDisplay/NumberDisplay";
import {
  isReadOnly,
  shouldTruncate,
} from "../TextTruncate/TextTruncate.utility";
import { ValueDisplay } from "../ValueDisplay/ValueDisplay";
import {
  type IBTNumberInputProps,
  type ValueType,
} from "./BTNumberInput.types";

/** Use this to override the internal styles set in BTNumberInput with Antd's styles */
export const defaultAntStyle = {};

const BTNumberInputInternal = track((props: { "data-testid": string }) => ({
  element: "Number Input",
  uniqueId: props["data-testid"],
}))(function <FormValues = undefined>({
  id,
  min,
  max,
  "data-testid": testid,
  readOnly,
  value,
  onChange,
  onBlur,
  roundToDecimalPlaces,
  allowNull = false,
  removeTrailingZerosOnEdit = false,
  addonAfter,
  currencyLocale = CurrencyLocale.default,
  disabled,
  ...otherProps
}: IBTNumberInputProps<FormValues>) {
  const isDirtyInternalRef = useRef(false);
  const { trackEvent } = useTracking();

  let inputValue: ValueType | undefined = value ?? undefined;
  if (
    !isDirtyInternalRef.current &&
    removeTrailingZerosOnEdit &&
    !isNullOrUndefined(value)
  ) {
    const decimalCount = Math.min(
      getAccuracy(value),
      otherProps.precision ?? 2,
    );
    // We need to display only up to {precision} number of decimals in edit mode,
    // even before the user makes a change
    inputValue = value.toFixed(decimalCount);
  }

  function handleChangeInternal(value: number | string | null | undefined) {
    trackEvent({
      event: "ValueChange",
      uniqueId: testid,
    });

    isDirtyInternalRef.current = true;
    if (allowNull && value === null) {
      onChange(id, null);
      return;
    }

    const valueNotNull = value === null ? undefined : value;

    let inputAsNumber = Number(valueNotNull);
    if (isNaN(inputAsNumber)) {
      // Invalid user input. Try to default to 0 if possible
      onChange(String(id), Math.min(Math.max(0, min), max));
      return;
    }

    if (roundToDecimalPlaces !== undefined) {
      inputAsNumber = round(inputAsNumber, roundToDecimalPlaces);
    }

    // We do not want validation errors for when number input is out of range.
    // Ant Design constrains the value between the min and max range, but only on blur,
    // meaning a validation error will occur/display until then
    const constrainedInput = Math.min(Math.max(inputAsNumber, min), max);

    onChange(String(id), constrainedInput);
  }

  function handleBlur() {
    if (onBlur) {
      onBlur(id, true);
    }
  }

  function removeTrailingZeros(value: ValueType | undefined) {
    if (value && typeof value === "string") {
      const noZeros = parseFloat(value);
      if (isNaN(noZeros)) {
        return "";
      }
      return noZeros.toString();
    }
    return "";
  }

  if (isReadOnly(readOnly)) {
    let displayValue;
    if (!isNullOrUndefined(value)) {
      displayValue = (
        <NumberDisplay
          data-testid={testid}
          value={value}
          shouldTruncate={shouldTruncate(readOnly)}
          decimalScale={otherProps.precision ?? Math.max(0, getAccuracy(value))}
          suffix={typeof addonAfter === "string" ? addonAfter : undefined}
          currencyLocale={currencyLocale}
        />
      );
    }

    return (
      <ValueDisplay
        id={id}
        data-testid={testid}
        tabIndex={otherProps.tabIndex}
        value={displayValue}
      />
    );
  }

  const pattern = `[0-9\\${currencyLocale.decimalSeparator}\\-]*`;

  return (
    <BuiNumberInput
      id={id}
      name={id}
      data-testid={testid}
      value={inputValue}
      decimalSeparator={currencyLocale.decimalSeparator}
      {...(removeTrailingZerosOnEdit && {
        formatter: (value: ValueType | undefined) => removeTrailingZeros(value),
      })}
      onChange={handleChangeInternal}
      inputMode={otherProps.precision === 0 ? "numeric" : "decimal"}
      pattern={pattern}
      onBlur={handleBlur}
      aria-disabled={disabled}
      disabled={disabled}
      {...otherProps}
      // addonAfter is destructured out of props (it doubles as the read-only
      // suffix), so it is not carried by otherProps — forward it explicitly.
      addonAfter={addonAfter}
    />
  );
});

export const BTNumberInput = memo(
  BTNumberInputInternal,
) as typeof BTNumberInputInternal;
